import { OrganizationService } from "@root/services";
import { useState } from "react";
import { useNotification } from "./useNotification";
import { TOAST_VARIANT } from "@root/constants";

export function useAdminHook() {
  const organizationService = new OrganizationService();
  const { showNotification } = useNotification();

  const [organizations, setOrganizations] = useState<{
    rows: any[];
    [key: string]: any;
  }>({ rows: [] });
  const [loader, setLoader] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState<string | Date | null>(null);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const pageSize = 10; // Page size for pagination

  const fetchOrganizations = async (payload: any = {}, currentPage?: number) => {
    setLoader(true);
    const query: any = { ...payload };
    if (search && search.trim() !== "") query.search = search;
    // Do NOT add date to query params; add to payload only
    let bodyPayload = { ...query };
    if (selectedDate) {
      const dateObj =
        typeof selectedDate === "string"
          ? new Date(selectedDate)
          : selectedDate;
      if (dateObj && !isNaN(dateObj.getTime())) {
        // Format as YYYY-MM-DD in local time, not UTC
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        bodyPayload.date = `${year}-${month}-${day}`;
      }
    }
    // Use currentPage parameter if provided, otherwise use state page
    const pageToUse = currentPage !== undefined ? currentPage : page;
    // Calculate offset and limit for pagination
    // Page 1: offset = 0, limit = 10
    // Page 2: offset = 10, limit = 10
    // Page 3: offset = 20, limit = 10
    bodyPayload.offset = (pageToUse - 1) * pageSize;
    bodyPayload.limit = pageSize;
    try {
      const response = await organizationService.fetchOrganizations(
        bodyPayload
      );
      setOrganizations(response?.data?.data || []);
      
      // Extract count from response.data.data.count (based on actual API response structure)
      const totalCount =
        response?.data?.data?.count ||
        response?.totalCount ||
        response?.data?.totalCount ||
        response?.data?.pagination?.totalCount ||
        response?.data?.data?.totalCount ||
        response?.data?.total ||
        0;
      
      setPageCount(totalCount);
    } finally {
      setLoader(false);
    }
  };

  const createAdmin = async (data: any) => {
    setLoader(true);
    try {
      const response = await organizationService.createAdmin(data);
      if (response?.data?.data) {
        fetchOrganizations();
        showNotification({
          type: TOAST_VARIANT.SUCCESS,
          message: "Admin created successfully",
        });
      }
    } catch (error: any) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: error?.response?.data?.message || "Failed to create admin",
      });
    } finally {
      setLoader(false);
    }
  };

  const updateAdmin = async (data: any) => {
    setLoader(true);
    try {
      const response = await organizationService.updateAdmin(data);
      if (response?.data?.data) {
        fetchOrganizations();
        showNotification({
          type: TOAST_VARIANT.SUCCESS,
          message: "Admin updated successfully",
        });
      }
    } catch (error: any) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: error?.response?.data?.message || "Failed to update admin",
      });
    } finally {
      setLoader(false);
    }
  };

  const deleteAdmin = async (data: any) => {
    setLoader(true);
    try {
      const response = await organizationService.deleteAdmin(data);
      if (response?.data?.data) {
        fetchOrganizations();
        showNotification({
          type: TOAST_VARIANT.SUCCESS,
          message: "Admin deleted successfully",
        });
      }
    } catch (error: any) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: error?.response?.data?.message || "Failed to delete admin",
      });
    } finally {
      setLoader(false);
    }
  };

  return {
    fetchOrganizations,
    organizations,
    setOrganizations,
    createAdmin,
    updateAdmin,
    deleteAdmin,
    loader,
    setLoader,
    search,
    setSearch,
    selectedDate,
    setSelectedDate,
    page,
    setPage,
    pageCount,
    setPageCount,
  };
}

export default useAdminHook;
